/* eslint-disable complexity */
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { NarrowedEntityData } from '@adornis/bh-util/selectionset-types.js';
import { format } from 'date-fns';
import { toBerlinDate } from '../util.js';
import type { versionDiffTeachingUserSelSet } from '../api/product.js';
import { bhBaseQlContext } from '../bh-context.js';
import type { Appointment } from '../entities/appointment.js';
import { isHexColor } from '../entities/ColorDefinitions.js';
import type { Participation } from '../entities/Participation.js';
import type { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import type { Product } from '../entities/products.js';
import type { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import { ParticipationState } from '../enums/enums.js';
import { translations } from '../translations/translation.js';

const getMailColors = () => {
  const { accentColor, accentContrastFontColor } = bhBaseQlContext?.colorDefinitions?.mailColorDefinitions ?? {};
  return {
    accentColor: isHexColor(accentColor) ? accentColor : '#00628f',
    accentContrastFontColor: isHexColor(accentContrastFontColor) ? accentContrastFontColor : '#ffffff',
  };
};

export const buildProductCard = (
  name: string,
  dateStart: Maybe<Date>,
  dateEnd: Maybe<Date> = null,
  notYetDetermined: boolean = false,
  location: string | null = null,
  lang: 'de' | 'en' = 'de',
) => {
  const w100Style = 'style="width:100%; padding-left:8px; font-weight:bold;"';
  const locale = lang === 'de' ? 'de-DE' : 'en-US';
  const start = dateStart?.toLocaleString(locale, { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' });
  const end = dateEnd?.toLocaleString(locale, { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' });
  const { accentColor: mailAccentColor, accentContrastFontColor: mailAccentContrastFontColor } = getMailColors();

  const tr_start = lang === 'de' ? 'Start' : 'Start';
  const tr_end = lang === 'de' ? 'Ende' : 'End';
  const tr_location = lang === 'de' ? 'Ort' : 'Location';

  return `
  <div style="padding: 10px; border-radius: 5px; background-color: ${mailAccentColor}; color: ${mailAccentContrastFontColor};">
      <table bgcolor="${mailAccentColor}" style="width: 100%; padding: 10px; border-radius: 5px; background-color: ${mailAccentColor};">
        <tbody>
          <tr><td colspan="2"><h3 style="color: ${mailAccentContrastFontColor}; font-weight:500; margin-top: 0 !important; border-bottom: 2px solid ${mailAccentContrastFontColor}; margin-bottom: 4px;">${name}</h3></td></tr>
                  ${(() => {
                    if (notYetDetermined) return '<tr> <td colspan="2"> ist noch nicht festgelegt </td></tr>';

                    return `
                      <tr> <td> <span> ${tr_start}: </span> </td> <td ${w100Style}> ${start} </td> </tr>
                      ${dateEnd ? `<tr> <td> <span> ${tr_end}: </span> </td> <td ${w100Style}> ${end} </td> </tr>` : ''}
                      ${
                        location
                          ? `<tr> <td> <span> ${tr_location}: </span> </td> <td ${w100Style}> ${location} </td> </tr>`
                          : ''
                      }
                    `;
                  })()}
        </tbody>
      </table>
  </div>
  `;
};

export function buildProductCardText(args: {
  name: string;
  entries: { label?: string; start?: Date | null; end?: Date | null; location?: string | null }[];
  notYetDetermined?: boolean;
  lang?: 'de' | 'en';
}): string {
  const locale = args.lang === 'en' ? 'en-US' : 'de-DE';
  const clean = (text: string) => text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const cap = (text: string) => (text.length > 450 ? `${text.slice(0, 449).trimEnd()}…` : text);
  const prefix = `${clean(args.name)}: `;
  if (args.notYetDetermined) {
    return cap(`${prefix}${args.lang === 'en' ? 'Date to be determined' : 'Termin steht noch nicht fest'}`);
  }

  const formatDate = (date: Date) =>
    clean(date.toLocaleString(locale, { timeZone: 'Europe/Berlin', dateStyle: 'medium', timeStyle: 'short' }));
  const berlinDay = (date: Date) => date.toLocaleDateString('en-US', { timeZone: 'Europe/Berlin' });
  const entries = args.entries.map(({ label, start, end, location }) => {
    const endText = end
      ? start && berlinDay(start) === berlinDay(end)
        ? clean(end.toLocaleTimeString(locale, { timeZone: 'Europe/Berlin', timeStyle: 'short' }))
        : formatDate(end)
      : '';
    const place = clean(location ?? '');
    return clean(
      `${clean(label ?? '')} ${start ? formatDate(start) : ''}${endText ? `–${endText}` : ''}${place ? ` (${place})` : ''}`,
    );
  });

  let text = prefix;
  let result = cap(prefix);
  for (const [index, entry] of entries.entries()) {
    text += `${index ? '; ' : ''}${entry}`;
    const remaining = entries.length - index - 1;
    const tail = remaining ? ` +${remaining} ${args.lang === 'en' ? 'more dates' : 'weitere Termine'}` : '';
    // Reserve space for the omitted-date count as part of the 450-character limit.
    if (text.length > 450) return index === 0 ? cap(text) : result;
    if (text.length + tail.length <= 450) result = text + tail;
    else if (index === 0) result = cap(text.slice(0, 449 - tail.length).trimEnd() + '…' + tail);
  }
  return result;
}

export function buildWhatsappDateList(args: {
  entries: { start?: Date | null; end?: Date | null }[];
  notYetDetermined?: boolean;
}): string {
  if (args.notYetDetermined) return 'Termin steht noch nicht fest';
  const entries = args.entries
    .filter((entry): entry is { start: Date; end?: Date | null } => !!entry.start)
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .map(({ start, end }) => {
      const berlinStart = toBerlinDate(start);
      const startText = format(berlinStart, 'dd.MM.yyyy HH:mm');
      if (!end) return startText;
      const berlinEnd = toBerlinDate(end);
      const sameDay = format(berlinStart, 'yyyy-MM-dd') === format(berlinEnd, 'yyyy-MM-dd');
      return `${startText}–${format(berlinEnd, sameDay ? 'HH:mm' : 'dd.MM.yyyy HH:mm')}`;
    });
  if (!entries.length) return 'Termin steht noch nicht fest';

  let text = '';
  let result = '';
  for (const [index, entry] of entries.entries()) {
    text += `${index ? ', ' : ''}${entry}`;
    const remaining = entries.length - index - 1;
    const tail = remaining ? ` +${remaining} weitere Termine` : '';
    if (text.length > 450) break;
    if (text.length + tail.length <= 450) result = text + tail;
  }
  return result;
}

export const buildRegistrationsHTML = (
  states: Array<{
    name: string;
    state: ParticipationState;
    topic: Maybe<string>;
    language: Maybe<string>;
    waitingListPlace?: number | null;
    appointments: NarrowedEntityData<Appointment, { name: 1; start: 1; end: 1; location: 1 }>[];
    englishVersion?: boolean;
  }>,
  highlight = true,
) => {
  const singleCard = (
    state: {
      name: string;
      state: ParticipationState;
      topic: Maybe<string>;
      language: Maybe<string>;
      waitingListPlace?: Maybe<number>;
      appointments: NarrowedEntityData<Appointment, { name: 1; start: 1; end: 1; location: 1 }>[];
      englishVersion?: boolean;
    },
    highlight = false,
  ) => {
    const defaultFormatString = 'dd. MMM yyyy, HH:mm';
    const translationIdx = state.englishVersion ? 1 : 0;
    const w100Style = 'style="width:100%; padding-left:8px; font-weight:bold;"';
    const { accentColor: mailAccentColor, accentContrastFontColor: mailAccentContrastFontColor } = getMailColors();

    const toFormat = (date: Date | null | undefined, formatStr: string) => {
      if (!date) return '';
      return format(toBerlinDate(date), formatStr);
    };

    return `
    <table bgcolor="${highlight ? mailAccentColor : '#efefef'}" style="width: 100%; background-color: ${highlight ? mailAccentColor : '#efefef'}; color: ${
      highlight ? mailAccentContrastFontColor : '#000000'
    }; padding: 10px; border-radius: 5px;">
      <tbody>
        <tr><td colspan="2"><span style="font-size: 24px; font-weight:500; display: block; width: 100%; margin-top: 0 !important; border-bottom: 2px solid ${mailAccentContrastFontColor};
    margin-bottom: 4px;">${state.name}</span></td></tr>
        ${
          state.state === ParticipationState.participated
            ? ` <tr> <td> <span> ${translations.mail_template.participated[translationIdx]} </span> </td> </tr> `
            : ` <tr> <td> <span style="white-space: nowrap"> ${
                translations.mail_template.status[translationIdx]
              }: </td><td ${w100Style}> ${
                translations.WORKSHOPS.STATES[state.state][translationIdx]
              } </span> </td> </tr>`
        }

          ${
            state.topic
              ? `<tr><td style="white-space: nowrap; width: min-content">${translations.mail_template.topic[translationIdx]}: </td><td ${w100Style}> ${state.topic}</td></tr>`
              : ''
          }
          ${
            state.language
              ? `<tr><td style="white-space: nowrap; width: min-content">${
                  translations.mail_template.language[translationIdx]
                }: </td><td ${w100Style}> ${translations.languages[state.language][translationIdx]}</td></tr>`
              : ''
          }
        ${
          state.waitingListPlace
            ? `<tr><td style="white-space: nowrap; width: min-content">${translations.mail_template.waitinglistPlace[translationIdx]}: </td><td ${w100Style}> ${state.waitingListPlace} </td></tr>`
            : ''
        }
        ${state.appointments
          .map(
            (a, idx) => `
            ${
              state.appointments.length > 1
                ? `
                  <tr><td colspan="2">
                    <h4 style="margin-top: 8px !important;padding: 8px 0 0 0 !important;border-top:1px dashed;">${
                      a.name ?? idx + 1 + '. Termin'
                    }</h4>
                  </td></tr> 
                `
                : ''
            }
                ${(() => {
                  if (a.start === null) {
                    return `<tr>
                      <td> ${translations.mail_template.date[translationIdx]} </td>
                      <td> <i> TBD </i> </td>
                    </tr>`;
                  }

                  return `
                    <tr><td>${translations.mail_template.start[translationIdx]}: </td><td ${w100Style}> ${toFormat(
                    a.start,
                    defaultFormatString,
                  )}</td></tr>
                    <tr><td>${translations.mail_template.end[translationIdx]}: </td><td ${w100Style}> ${toFormat(
                    a.end,
                    defaultFormatString,
                  )}</td></tr>
                  `;
                })()}
          <tr><td>${translations.mail_template.location[translationIdx]}: </td><td ${w100Style}> ${
              a.location ?? ''
            }</td></tr>
        `,
          )
          .join('')}
      </tbody>
    </table>
    <table style="min-height: 10px; height: 10px; width: 100%"></table>
    `;
  };

  if (states.length > 1) return `${states.map(state => `<p> ${singleCard(state, highlight)} </p>`).join('')}`;
  if (states[0]) return singleCard(states[0], highlight);
  return '';
};

export type BuildProductTeachingsChange = {
  old: Partial<NarrowedEntityData<User, typeof versionDiffTeachingUserSelSet>>[];
  new: Partial<NarrowedEntityData<User, typeof versionDiffTeachingUserSelSet>>[];
};
export type BuildProductChanges = {
  appointments?: Array<{ old: PartialEntityData<Appointment>; new: PartialEntityData<Appointment> }>;
  teachings?: BuildProductTeachingsChange;
  [key: string]:
    | { old: any; new: any }
    | Array<{ old: PartialEntityData<Appointment>; new: PartialEntityData<Appointment> }>
    | undefined;
};

type ChangeRowRenderType = (product: Product, changes: any, key: string, englishVersion: boolean) => string | null;
const getSimpleChangeRow: ChangeRowRenderType = (
  product: Product,
  changes: any,
  key: string,
  englishVersion: boolean,
) => {
  const oldValue = changes[key]?.old;
  const newValue = changes[key]?.new;
  const fallbackValue = product[key] || '-';
  console.log('>>>>>>>>>>> ', oldValue, newValue);
  return tableRow(key, oldValue, newValue, fallbackValue, englishVersion);
};

const getListChangeRow: ChangeRowRenderType = (
  product: Product,
  changes: BuildProductChanges,
  key: string,
  englishVersion: boolean,
) => {
  const targetValue = changes.teachings;

  if (!targetValue) return null;

  return `
    <tr>
      <td> <b>${tableKeyname('lecturer', englishVersion)}</b> </td>
      <td>
        ${targetValue.old.map(t => tableChangesOld(`${t.title ? `${t.title} ` : ''}${t.firstName} ${t.lastName}`))}
        <br />
        ${targetValue.new.map(t => noChangeTableElem(`${t.title ? `${t.title} ` : ''}${t.firstName} ${t.lastName}`))}
      </td>
    </tr>
  `;
};

export const buildProductChangesHtml = (
  product: Maybe<Product>,
  changes: BuildProductChanges,
  englishVersion: boolean,
) => {
  if (!product) {
    throw new Error('No valid Product was given for building the changes html.');
  }
  logger.info({ changes }, '>>>>> building changes table');

  // product changes
  const rows = [
    { field: 'name', renderFn: getSimpleChangeRow },
    { field: 'teachings', renderFn: getListChangeRow },
    { field: 'infoLink', renderFn: getSimpleChangeRow },
    { field: 'capacity', renderFn: getSimpleChangeRow },
    { field: 'topic', renderFn: getSimpleChangeRow },
  ].map(({ field, renderFn }) => {
    if (field === 'appointments') return null;
    return renderFn(product, changes, field, englishVersion);
  });

  // appointment changes
  const appointmentRows: string[] = [];
  if (product instanceof Workshop) {
    const renderAppointment = (
      foundIdx: number,
      foundAppointment?: PartialEntityData<Appointment>,
      change?: { old: any; new: any },
    ) => {
      appointmentRows.push(
        `<tr><td><h4 style="margin-top: 10px !important;margin-bottom: 0 !important">${
          change &&
          change.old._id &&
          change.new._id &&
          (change.old.name || change.new.name) &&
          change.old.name !== change.new.name
            ? `<i><s>${change.old.name}</s></i> <i>${change.new.name}</i>`
            : `${
                foundAppointment?.name ??
                foundIdx + 1 + `. ${translations.mail_template.appointment[englishVersion ? 1 : 0]}`
              } ${change ? appointmentStatusDisplay(change.old._id, change.new._id, englishVersion ? 1 : 0) : ''}`
        }</h4></td></tr>`,
      );

      const dateFormat = 'dd.MM.yyyy, HH:mm';

      let oldStart = change?.old.start ? format(toBerlinDate(change?.old?.start), dateFormat) : undefined;
      let newStart = change?.new.start ? format(toBerlinDate(change?.new?.start), dateFormat) : undefined;

      // TODO: clean this up
      const _start = foundAppointment?.start as Maybe<Date>;
      const _end = foundAppointment?.end as Maybe<Date>;
      if (_start && !(_start instanceof Date)) throw new Error('Date expected');
      if (_end && !(_end instanceof Date)) throw new Error('Date expected');

      appointmentRows.push(
        tableRow('start', oldStart, newStart, _start ? format(toBerlinDate(_start), dateFormat) : 'TBD', englishVersion),
      );

      const oldEnd = change?.old.end ? format(toBerlinDate(change.old.end), dateFormat) : undefined;
      const newEnd = change?.new.end ? format(toBerlinDate(change.new.end), dateFormat) : undefined;
      appointmentRows.push(tableRow('end', oldEnd, newEnd, _end ? format(toBerlinDate(_end), dateFormat) : 'TBD', englishVersion));

      appointmentRows.push(
        tableRow(
          'location',
          change?.old.location as string,
          change?.new.location as string,
          foundAppointment?.location ?? '',
          englishVersion,
        ),
      );

      // todo: dozent
    };

    // iterate all currently existing appointments
    product.appointments
      .map((foundAppointment, foundIdx) => {
        const change = changes.appointments?.find(change => {
          const productID = change.old._id ?? change.new._id;
          if (!productID) throw new Error('There must be at least one ID given in the changes.');
          return productID === foundAppointment._id;
        });

        renderAppointment(foundIdx, foundAppointment, change);
      })
      .join('');

    // iterate all deleted appointments
    changes.appointments
      ?.filter(change => !change.new._id && change.old._id)
      .map((change, i) => {
        renderAppointment(product.appointments.length + i, undefined, change);
      })
      .join('');
  }

  const { accentColor: mailAccentColor, accentContrastFontColor: mailAccentContrastFontColor } = getMailColors();
  return `<table bgcolor="${mailAccentColor}" style="width: 100%; background-color: ${mailAccentColor}; color: ${mailAccentContrastFontColor}; padding: 10px; border-radius: 5px;"> <tbody> ${rows.join(
    '\n',
  )}${appointmentRows.join('\n')} </tbody> </table>`;
};

export const buildBookableWorkshopsHTML = (
  workshops: NarrowedEntity<
    Workshop,
    { _id: 1; institutionID: 1; name: 1; language: 1; appointments: { name: 1; start: 1; end: 1; location: 1 } }
  >[],
  englishVersion: boolean,
) => {
  const singleCard = (
    workshop: NarrowedEntity<
      Workshop,
      {
        _id: 1;
        institutionID: 1;
        name: 1;
        language: 1;
        appointments: { name: 1; start: 1; end: 1; location: 1 };
      }
    >,
  ) => {
    const translationIdx = englishVersion ? 1 : 0;
    const w100Style = 'style="width:100%; padding-left:8px; font-weight:bold;"';
    const url = A.absoluteUrl(
      '/registration?step=personal&institution=' +
        workshop.institutionID +
        '&data=%7B%22_class%22%3A%22RegistrationData%22%2C%22institutionID%22%3A%22testing_institution_2%22%2C%22productData%22%3A%5B%7B%22_class%22%3A%22RegistrationWorkshopData%22%2C%22_id%22%3A%2266fd143d20e1ae0ba7020%22%2C%22productID%22%3A%22' +
        workshop._id +
        '%22%7D%5D%2C%22acceptedTerms%22%3Afalse%2C%22userStammdaten%22%3Anull%2C%22user%22%3Anull%7D',
    );
    const { accentColor: mailAccentColor, accentContrastFontColor: mailAccentContrastFontColor } = getMailColors();

    const toFormat = (date: Date | null | undefined, formatStr: string) => {
      if (!date) return '';
      return format(toBerlinDate(date), formatStr);
    };
    return `
    <table bgcolor="${mailAccentColor}" style="width: 100%; background-color: ${mailAccentColor}; color: ${mailAccentContrastFontColor}; padding: 10px; border-radius: 5px;">
      <tbody>
        <tr><td colspan="2"><h3 style="color: ${mailAccentContrastFontColor}; font-weight:500; margin-top: 0 !important; border-bottom: 2px solid ${mailAccentContrastFontColor};
    margin-bottom: 4px;">${workshop.name}</h3></td></tr>
          ${
            workshop.language
              ? `<tr><td>${translations.mail_template.language[translationIdx]}: </td><td ${w100Style}> ${
                  translations.languages[workshop.language][translationIdx]
                }</td></tr>`
              : ''
          }
        ${workshop.appointments
          .map(
            (a, idx) => `
            ${
              workshop.appointments.length > 1
                ? `
                  <tr><td colspan="2">
                    <h4 style="margin-top: 8px !important;padding: 8px 0 0 0 !important;border-top:1px dashed;">${
                      a.name ?? idx + 1 + '. Termin'
                    }</h4>
                  </td></tr> 
                `
                : ''
            }
                ${(() => {
                  if (a.start === null) {
                    return `<tr>
                      <td> ${translations.mail_template.date[translationIdx]} </td>
                      <td> <i> TBD </i> </td>
                    </tr>`;
                  }

                  return `
                    <tr>
                      <td>${translations.mail_template.start[translationIdx]}: </td>
                      <td ${w100Style}> ${a.start ? toFormat(a.start, 'dd. MMM yyyy, HH:mm') : ''}</td>
                    </tr>
                    <tr>
                      <td>${translations.mail_template.end[translationIdx]}: </td>
                      <td ${w100Style}> ${a.end ? toFormat(a.end, 'dd. MMM yyyy, HH:mm') : ''}</td>
                    </tr>`;
                })()}
          <tr><td>${translations.mail_template.location[translationIdx]}: </td><td ${w100Style}> ${
              a.location ?? ''
            }</td></tr>
          <tr><td colspan='2'><a href='${url}' target='_blank' style='color: ${mailAccentContrastFontColor};' >mehr Infos</a></td><td></td></tr>
        `,
          )
          .join('')}
      </tbody>
    </table>
    <table style="min-height: 10px; height: 10px; width: 100%"></table>
    `;
  };

  return `${workshops
    .toSorted(
      (a, b) => (a.appointments[0]?.start ?? new Date()).getTime() - (b.appointments[0]?.start ?? new Date()).getTime(),
    )
    .map(workshop => `<p> ${singleCard(workshop)} </p>`)
    .join('')}`;
};

const appointmentStatusDisplay = (oldID: Maybe<string>, newID: Maybe<string>, translationIdx: 0 | 1) => {
  if (!oldID && !newID) throw new Error("There should be at least one of the ID's in the changes.");
  if (oldID && newID) return '';
  return `<b style="color: ${!oldID ? '#50dd50' : 'red'}"> ${
    !oldID ? translations.mail_template.new[translationIdx] : translations.mail_template.removed[translationIdx]
  }</b>`;
};

const tableRow = (
  key: string,
  oldValue: Maybe<string>,
  newValue: Maybe<string>,
  fallbackValue: string,
  englishVersion: boolean,
) => {
  if ((!!oldValue || !!newValue) && oldValue !== newValue) {
    return `<tr><td>${tableKeyname(key, englishVersion)}</td> <td>${tableChanges(oldValue, newValue)}</td><tr>`;
  } else {
    return `<tr><td>${tableKeyname(key, englishVersion)}</td> <td>${noChangeTableElem(fallbackValue)}</td></tr>`;
  }
};

const tableKeyname = (fieldKeyName: string, englishVersion: boolean) => {
  const keyName = (translations.field[fieldKeyName]?.[englishVersion ? 1 : 0] as string | undefined) ?? fieldKeyName;
  return `<b>${keyName}:</b>`;
};

const noChangeTableElem = (value: Maybe<string>) => {
  return `<i>${value ?? ''}</i>`;
};
const tableChangesOld = (value: Maybe<string>) => {
  return `<i><s>${value ?? ''}</s><i>`;
};
const tableChanges = (oldValue: Maybe<string>, newValue: Maybe<string>) => {
  return `${tableChangesOld(oldValue ?? '')} ${noChangeTableElem(newValue ?? '')}`;
};

export const buildLoginLink = (token: string) => {
  return `
    <a href="${A.absoluteUrl('/action', new URLSearchParams({ token, mode: 'login' }))}">zum Profil</a>
  `;
};

const buildDeregisterUrlParams = (token: string, participationID: string) =>
  new URLSearchParams({ token, mode: 'deregister', participationID });

export const buildDeregisterUrlSuffix = (token: string, participationID: string): string =>
  'action?' + buildDeregisterUrlParams(token, participationID).toString();

export const buildDeregisterUrl = (token: string, participationID: string): string => {
  return A.absoluteUrl('/action', buildDeregisterUrlParams(token, participationID));
};

export const buildDeregisterLink = (token: string, participationID: string, lang: 'de' | 'en' = 'de') => {
  const text = lang === 'en' ? 'Deregister now' : 'Jetzt abmelden';
  return `
    <a href="${buildDeregisterUrl(token, participationID)}">${text}</a>
  `;
};

export const buildNewsletterOptinLink = (token: string, lang: 'de' | 'en' = 'de') => {
  const text = lang === 'en' ? 'Confirm newsletter' : 'Newsletter bestätigen';
  return `
    <a href="${A.absoluteUrl('/action', new URLSearchParams({ token, mode: 'newsletter-optin' }))}">${text}</a>
  `;
};

export const buildOTP = (code: string) => {
  return `<b> ${code} </b>`;
};

export const buildConfirmMail = (link: string) => {
  return `<a href="${link}">Hier klicken</a>`;
};

export const buildForgotPassword = (link: string) => {
  return `<a href="${link}">Hier klicken</a>`;
};

const getAppointmentsForParticipationSelectionSetParticipation = {
  product: {
    _id: 1,
    _class: 1,
    appointments: { start: 1 },
    slots: { _id: 1, start: 1, end: 1 },
    settings: { courseLocation: 1, inheritedCourseLocation: 1 },
  },
  timeslotID: 1,
  appointments: { start: 1 },
} as const;
export const getAppointmentsForParticipation = <
  T extends NarrowedEntityData<Participation, typeof getAppointmentsForParticipationSelectionSetParticipation>,
>(
  participation: Maybe<T>,
) => {
  if (!participation?.product) throw new Error('product is undefined');

  if (participation.product._class === 'Workshop') {
    const product = participation.product as Workshop;
    return product.appointments.sort((a, b) =>
      (a.start ?? new Date()).getTime() > (b.start ?? new Date()).getTime() ? 1 : -1,
    );
  }

  if (participation.product._class === 'Einzelberatung') {
    const _participation = participation as any as ParticipationEinzelberatung;
    const timeslotID = _participation.timeslotID;
    const product = _participation.product;
    const slot = product?.slots.find(slot => slot._id === timeslotID);
    const location = product?.settings.courseLocation ?? product?.settings.inheritedCourseLocation?.[1];
    return [{ start: slot?.start, end: slot?.end, location, name: '' }];
  }

  logger.error({ participation }, 'unreachable');
  throw new Error('unreachable');
};
